import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { skillColor } from "@/lib/skills";
import { Check, Lock, Type, ListChecks } from "lucide-react";
import { t } from "@/i18n";

export default function LearnCenter() {
  const [themes, setThemes] = useState([]);
  const [done, setDone] = useState(new Set());
  const [alphabetPct, setAlphabetPct] = useState(0);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api
      .get("/content/themes")
      .then((r) => setThemes(r.data))
      .catch(() => {});
    api
      .get("/content/roadmap")
      .then((r) => {
        const ids = [];
        r.data.forEach((sec) =>
          (sec.nodes || []).forEach((n) => {
            if (n.status === "completed") ids.push(n.id);
          }),
        );
        setDone(new Set(ids));
      })
      .catch(() => {});
    api
      .get("/progress/summary")
      .then((r) => setAlphabetPct(r.data?.alphabet_mastery_pct ?? 0))
      .catch(() => {});
  }, []);

  const alphabetFirst = !!user?.reading_beginner && alphabetPct < 80;

  return (
    <div className="max-w-4xl mx-auto pb-12 px-4">
      <h1 className="font-head text-3xl font-bold mb-6">{t("learncenter.apprendre")}</h1>

      {/* Boutons principaux géants en pile verticale */}
      <div className="flex flex-col gap-4 mb-10">
        {/* BLOC 1 : ALPHABET */}
        <button
          onClick={() => nav("/practice/alphabet")}
          className={`flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-5 rounded-[2rem] border-2 p-6 sm:p-8 transition hover:-translate-y-1 hover:shadow-lg ${
            alphabetFirst
              ? "border-amber-500 bg-amber-500/10"
              : "bg-card hover:border-amber-500/50"
          }`}
        >
          <span className="w-20 h-20 rounded-3xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-md">
            <Type size={40} />
          </span>
          <div className="flex-1 w-full">
            <div className="font-head text-2xl sm:text-3xl font-extrabold mb-1">
              {t("dashboard.apprendre_l_alphabet")}
            </div>
            <div className="text-base sm:text-lg text-muted-foreground mb-4">
              {alphabetPct}{t("learncenter.maitrise")}
            </div>
            <div className="h-3 sm:h-4 w-full rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full bg-amber-500 transition-all duration-500"
                style={{ width: `${alphabetPct}%` }}
              />
            </div>
          </div>
        </button>

        {/* BLOC 2 : MOTS (VOCABULAIRE) */}
        <button
          onClick={() => nav("/vocab-quiz")}
          className={`flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-5 rounded-[2rem] border-2 p-6 sm:p-8 transition hover:-translate-y-1 hover:shadow-lg ${
            alphabetFirst
              ? "bg-card hover:border-primary/50"
              : "border-primary bg-primary/10"
          }`}
        >
          <span className="w-20 h-20 rounded-3xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-md">
            <ListChecks size={40} />
          </span>
          <div className="flex-1">
            <div className="font-head text-2xl sm:text-3xl font-extrabold mb-1">
              {t("dashboard.apprendre_des_mots")}
            </div>
            <div className="text-base sm:text-lg text-muted-foreground">
              {t("learncenter.quiz_de_vocabulaire_thematique")}
            </div>
          </div>
        </button>
      </div>

      {/* Leçons en mode Grille de Blocs (finis les espaces vides) */}
      <div className="space-y-10">
        {themes.map((t) => {
          const color = skillColor(t.skill);

          return (
            <div key={t.id} className="space-y-4">
              <h2 className="text-lg font-extrabold uppercase tracking-widest flex items-center gap-3">
                <span
                  className="w-4 h-4 rounded-full shrink-0"
                  style={{ background: color }}
                />
                {t.title}
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {t.lessons.map((l, i) => {
                  const isDone = done.has(l.id);
                  const locked =
                    i > 0 && !done.has(t.lessons[i - 1].id) && !isDone;

                  return (
                    <button
                      key={l.id}
                      data-testid={`lc-lesson-${l.id}`}
                      disabled={locked}
                      onClick={() => nav(`/learn/lesson/${l.id}`)}
                      className={`flex flex-col items-center justify-center gap-4 rounded-3xl border-2 p-5 text-center transition aspect-square ${
                        locked
                          ? "opacity-60 bg-muted/20 border-muted cursor-not-allowed"
                          : "hover:-translate-y-1 hover:shadow-md hover:border-primary bg-card"
                      }`}
                    >
                      <div
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold shadow-sm shrink-0"
                        style={{
                          background: isDone
                            ? "#16A34A" // Vert succès
                            : locked
                              ? "hsl(var(--muted-foreground)/0.3)" // Gris verrouillé
                              : color, // Couleur du thème
                        }}
                      >
                        {isDone ? (
                          <Check size={32} />
                        ) : locked ? (
                          <Lock size={28} />
                        ) : (
                          i + 1
                        )}
                      </div>
                      <span
                        className={`font-semibold text-sm sm:text-base leading-tight line-clamp-3 ${locked ? "text-muted-foreground" : "text-foreground"}`}
                      >
                        {l.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
